/**
 * Klaviyo Data Enrichment Integration
 * Detects Klaviyo on the site and extracts customer profile data
 * to enrich tracking events for other ad platforms.
 *
 * Standalone version for co-aurora.
 * Ported from shopper/src/scripts/lib/integrations/klaviyo.js
 */
(function() {
  'use strict';

  function Klaviyo(config) {
    this.klaviyoData = null;
    this.dataLastFetched = null;
    this.isKlaviyoAvailable = false;
    window.TrackingBase.call(this, Object.assign({
      name: 'Klaviyo',
      enrichAdPlatforms: true,
      includeClv: true,
      includeSegments: true,
      includeEngagement: true,
      cacheTimeout: 300000
    }, config));
  }

  Klaviyo.prototype = Object.create(window.TrackingBase.prototype);
  Klaviyo.prototype.constructor = Klaviyo;

  /* ---- Initialize ---- */

  Klaviyo.prototype.initialize = function() {
    this._detectKlaviyo();
    if (this.isKlaviyoAvailable) {
      this._loadKlaviyoData();
      this.log('Klaviyo data enrichment initialized');
    } else {
      this.log('Klaviyo not detected, enrichment disabled');
    }
  };

  /* Enrichment only - no direct tracking */
  Klaviyo.prototype.track = function() {
    this.log('Klaviyo is enrichment-only');
  };

  /* ---- Detection ---- */

  Klaviyo.prototype._detectKlaviyo = function() {
    this.isKlaviyoAvailable = !!(
      window._learnq ||
      window.klaviyo ||
      window.KlaviyoSubscribe ||
      document.querySelector('[data-klaviyo-embed]') ||
      document.querySelector('script[src*="klaviyo"]')
    );
  };

  /* ---- Data loading ---- */

  Klaviyo.prototype._loadKlaviyoData = function() {
    if (this._isCacheValid()) return this.klaviyoData;

    try {
      var data = {};

      /* Method 1: _learnq identify calls */
      if (window._learnq && Array.isArray(window._learnq)) {
        window._learnq.forEach(function(call) {
          if (Array.isArray(call) && call[0] === 'identify' && call[1] && typeof call[1] === 'object') {
            var d = call[1];
            if (d.$email) data.email = d.$email;
            if (d.$phone_number) data.phone = d.$phone_number;
            if (d.$first_name) data.first_name = d.$first_name;
            if (d.$last_name) data.last_name = d.$last_name;
            if (d.$city) data.city = d.$city;
            if (d.$region) data.state = d.$region;
            if (d.$zip) data.postal_code = d.$zip;
            if (d.$country) data.country = d.$country;
            if (d.total_spent !== undefined) data.total_spent = d.total_spent;
            if (d.order_count !== undefined) data.order_count = d.order_count;
            if (d.average_order_value !== undefined) data.average_order_value = d.average_order_value;
            if (d.predicted_clv !== undefined) data.predicted_clv = d.predicted_clv;
            if (d.email_engagement_score !== undefined) data.email_engagement_score = d.email_engagement_score;
            if (d.customer_segment) data.customer_segment = d.customer_segment;
            if (d.preferred_categories) data.preferred_categories = d.preferred_categories;
          }
        });
      }

      /* Method 2: localStorage */
      try {
        ['__kla_id', 'klaviyo_profile', 'klaviyo_customer'].forEach(function(key) {
          var stored = localStorage.getItem(key);
          if (stored) {
            try {
              var parsed = JSON.parse(stored);
              if (parsed && typeof parsed === 'object') Object.assign(data, parsed);
            } catch (e) { /* ignore */ }
          }
        });
      } catch (e) { /* localStorage unavailable */ }

      /* Method 3: Shopify customer enrichment */
      var shopifyData = this.getCustomerData();
      if (shopifyData.email) data.email = data.email || shopifyData.email;
      if (shopifyData.phone) data.phone = data.phone || shopifyData.phone;
      if (shopifyData.customer_id) data.shopify_customer_id = shopifyData.customer_id;
      if (shopifyData.order_count !== undefined) data.order_count = data.order_count || shopifyData.order_count;
      if (shopifyData.lifetime_value !== undefined) data.total_spent = data.total_spent || shopifyData.lifetime_value;
      if (shopifyData.is_new_customer !== undefined) data.is_new_customer = shopifyData.is_new_customer;
      if (shopifyData.customer_type) data.customer_type = shopifyData.customer_type;

      this.klaviyoData = data;
      this.dataLastFetched = Date.now();
      return data;
    } catch (err) {
      this.error('Failed to load Klaviyo data', err);
      return {};
    }
  };

  Klaviyo.prototype._isCacheValid = function() {
    return this.klaviyoData && this.dataLastFetched &&
      (Date.now() - this.dataLastFetched) < this.config.cacheTimeout;
  };

  /* ---- Public API ---- */

  Klaviyo.prototype.getEnrichedCustomerData = function() {
    if (!this.isKlaviyoAvailable || !this.config.enrichAdPlatforms) return {};

    var kd = this._loadKlaviyoData();
    var enriched = {};

    if (kd.email) enriched.email = kd.email;
    if (kd.phone) enriched.phone = kd.phone;
    if (kd.first_name) enriched.first_name = kd.first_name;
    if (kd.last_name) enriched.last_name = kd.last_name;
    if (kd.city) enriched.city = kd.city;
    if (kd.state) enriched.state = kd.state;
    if (kd.postal_code) enriched.postal_code = kd.postal_code;
    if (kd.country) enriched.country = kd.country;

    if (this.config.includeClv) {
      if (kd.predicted_clv !== undefined) enriched.predicted_clv = kd.predicted_clv;
      if (kd.total_spent !== undefined) enriched.total_spent = kd.total_spent;
      if (kd.average_order_value !== undefined) enriched.average_order_value = kd.average_order_value;
      if (kd.order_count !== undefined) enriched.order_count = kd.order_count;
    }

    if (this.config.includeSegments) {
      if (kd.customer_segment) enriched.customer_segment = kd.customer_segment;
      if (kd.preferred_categories) enriched.preferred_categories = kd.preferred_categories;
      if (kd.is_new_customer !== undefined) enriched.is_new_customer = kd.is_new_customer;
      if (kd.customer_type) enriched.customer_type = kd.customer_type;
    }

    if (this.config.includeEngagement) {
      if (kd.email_engagement_score !== undefined) enriched.email_engagement_score = kd.email_engagement_score;
    }

    return enriched;
  };

  Klaviyo.prototype.isAvailable = function() {
    return this.isKlaviyoAvailable;
  };

  Klaviyo.prototype.refreshData = function() {
    this.klaviyoData = null;
    this.dataLastFetched = null;
    return this._loadKlaviyoData();
  };

  /* Export */
  window.TrackingKlaviyo = Klaviyo;
})();
